import { Database, Tables } from "@punchup/data/supabase.types";

import { SpecialRow } from "./specials";
import { SupabaseClient } from "@supabase/supabase-js";
import { createSupabaseAdminClient } from "@/data/supabase";
import { logException } from "@/analytics/sentry";

export type UserSpecialReaction = Tables<"user_specials_reactions">;

/**
 * Batch server-side fetch of clap counts for multiple specials in a single query.
 * Returns a map of special_id → total clap count.
 */
export async function getTotalReactionCountsForSpecials(
  specialIds: string[],
  type: UserSpecialReaction["type"] = "clap"
): Promise<Record<string, number>> {
  if (specialIds.length === 0) return {};

  const supabase = createSupabaseAdminClient();
  const { data, error } = await supabase
    .from("user_specials_reactions")
    .select("special_id, count")
    .eq("type", type)
    .in("special_id", specialIds);

  if (error || !data) {
    logException(error, { tags: { type } });
    return {};
  }

  const counts: Record<string, number> = {};
  for (const row of data) {
    counts[row.special_id] = (counts[row.special_id] ?? 0) + row.count;
  }
  return counts;
}

export async function getTotalReactionCountForSpecial(
  supabase: SupabaseClient<Database>,
  specialId: SpecialRow["id"],
  type: UserSpecialReaction["type"]
): Promise<UserSpecialReaction["count"]> {
  const { status, data, error } = await supabase
    .from("user_specials_reactions")
    .select("*")
    .eq("type", type)
    .eq("special_id", specialId);

  if (error || !data) {
    logException(error, {
      tags: { specialId },
    });
    return 0;
  } else if (status === 200) {
    const sumOfReactions = data.reduce(
      (acc, reaction) => acc + reaction.count,
      0
    );
    return sumOfReactions;
  }

  // at this point, no error occurred inserting but status was not 200 OK
  throw new Error(`Error counting reactions for special ID ${specialId}`);
}

export async function getUserReactionCountForSpecial(
  supabase: SupabaseClient<Database>,
  specialId: SpecialRow["id"],
  type: UserSpecialReaction["type"]
): Promise<UserSpecialReaction["count"]> {
  const {
    data: { session },
  } = await supabase.auth.getSession();

  if (!session?.user) {
    logException(
      new Error(
        `No user session found when retrieving ${type} reactions for special`
      ),
      { tags: { specialId } }
    );
    return 0;
  }

  const { status, data, error } = await supabase
    .from("user_specials_reactions")
    .select("*")
    .eq("type", type)
    .eq("user_id", session.user.id)
    .eq("special_id", specialId);

  if (error || !data) {
    logException(error, {
      tags: { specialId, userId: session.user.id },
    });
    return 0;
  } else if (status === 200) {
    const sumOfReactions = data.reduce(
      (acc, reaction) => acc + reaction.count,
      0
    );
    return sumOfReactions;
  }

  // at this point, no error occurred inserting but status was not 200 OK
  throw new Error(
    `Error finding user reactions for special ID ${specialId} and user ID ${session.user.id}`
  );
}

export async function getUserReactionsForSpecial(
  supabase: SupabaseClient<Database>,
  specialId: SpecialRow["id"],
  type: UserSpecialReaction["type"]
): Promise<UserSpecialReaction[]> {
  const {
    data: { session },
  } = await supabase.auth.getSession();

  if (!session?.user) {
    logException(
      new Error(
        `No user session found when retrieving ${type} reactions for special`
      ),
      { tags: { specialId } }
    );
    return [];
  }

  const { status, data, error } = await supabase
    .from("user_specials_reactions")
    .select("*")
    .eq("type", type)
    .eq("user_id", session.user.id)
    .eq("special_id", specialId);

  if (error) {
    logException(error);
    return [];
  } else if (status === 200) {
    return data;
  }

  // at this point, no error occurred inserting but status was not 200 OK
  throw new Error(
    `Error finding user reactions for special ID ${specialId} and user ID ${session.user.id}`
  );
}

export async function createUserReactionForSpecial(
  supabase: SupabaseClient<Database>,
  specialId: SpecialRow["id"],
  type: UserSpecialReaction["type"],
  count: UserSpecialReaction["count"]
): Promise<UserSpecialReaction["count"]> {
  const {
    data: { session },
  } = await supabase.auth.getSession();

  if (!session?.user) {
    logException(
      new Error(
        `No user session found when creating ${type} reactions for special`
      ),
      {
        extra: {
          specialId,
          count,
        },
        tags: {
          specialId,
        },
      }
    );
    return 0;
  }

  const { status, data, error } = await supabase
    .from("user_specials_reactions")
    .insert({
      user_id: session.user.id,
      special_id: specialId,
      type,
      count,
    })
    .select();

  if (error || !data) {
    logException(error, {
      extra: {
        specialId,
        count,
      },
      tags: { specialId, userId: session.user.id },
    });
    return 0;
  } else if (status === 201) {
    const insertedRow = data[0];
    return insertedRow.count;
  }

  // at this point, no error occurred inserting but status was not 201 Created
  throw new Error(
    `Error adding user reaction type ${type} with count ${count} for special ID ${specialId} and user ID ${session.user.id}`
  );
}
